// Packages
import React, {
  MutableRefObject,
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo
} from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import { FormattedMessage } from 'react-intl';
// Components
import ModalCloseIcon from "../modalCloseIcon";
import DialingCodeSelector from "../dialingCodeSelector";
import MobileVerification from "../mobileVerification";
import CheckboxReCaptchaEnterprise from "../captchas/googleCaptchas/checkboxReCaptchaEnterprise";
// Icons, Images etc.
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleRight } from "@fortawesome/free-solid-svg-icons";
import { EMAIL_REGEX } from "../../helpers/constants";
// Redux Operations
import { userOperations } from "../../state/features/user";
// Contexts
import { EnvContext } from "../envContext";
import TermsOfServiceContainer from "../termsOfServiceContainer";

interface ISignInProps {
  onClose: () => void;
  company: any;
  captchaEnabled: boolean;
  smsDisabled: boolean;
  user: any;
  verifyUser: (data: any, csrfToken: string, captchaToken: string) => void;
  signUp: (data: any, token: string) => void;
}

function SignIn(props: ISignInProps) {
  const [show, setShow] = useState(true);
  const [userPayload, setUserPayload] = useState({} as any);
  const [dialingCode, setDialingCode] = useState("");
  const [pageType, setPageType] = useState("sign-in");
  const [signInError, setSignInError] = useState(null as any);
  const [hidePopup, setHidePopup] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaWidgetID, setCaptchaWidgetID] = useState(null);
  const [isTosChecked, setIsTosChecked] = useState(false);
  const [isMarketingConsentChecked, setIsMarketingConsentChecked] = useState(false);

  const emailRef = useRef() as MutableRefObject<HTMLInputElement>;

  const { googleRecaptchaSiteKey } = useContext(EnvContext);

  useEffect(() => {
    // Set Email from Localstorage if user email does not exist
    if (showEmailLogin && !userPayload.email && emailRef.current && localStorage.userEmail) {
      emailRef.current.value = localStorage.getItem("userEmail") as string;
      setUserPayload({
        ...userPayload,
        [emailRef.current.name]: emailRef.current.value,
      });
    }
  }, [showEmailLogin]);

  useEffect(() => {
    if (props.captchaEnabled && !captchaToken) {
      setSubmitButtonDisabled(true);
    } else {
      setSubmitButtonDisabled(false);
    }
  }, [props.captchaEnabled, captchaToken]);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      props.onClose(), 1000;
    }, 500);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    setSignInError(null);
    let value = e.target.value;
    if (e.target.name === "phone_number") {
      value = value.replace(/\D/g, "");
      e.target.value = value;
    }
    setUserPayload({ ...userPayload, [e.target.name]: value });
  };

  const validatePhoneNumber = (number: any) => {
    let regex = /^[0-9]{6,15}$/;
    return regex.test(number);
  };

  const validateEmail = (email: string) => {
    return EMAIL_REGEX.test(email);
  };

  const submitSignIn = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let payload = userPayload as any;
    let isPhoneNumberValid = validatePhoneNumber(payload.phone_number);
    payload.company_id = props.company.id;
    payload.dialing_code = dialingCode;
    payload.marketing_consent = isMarketingConsentChecked;

    if (props.captchaEnabled) {
      // Reset Captcha Widget
      grecaptcha.enterprise.reset(captchaWidgetID);
    }

    if (isPhoneNumberValid) {
      setHidePopup(true);
      const csrfToken = document.querySelector('[name=csrf-token]').content;
      props.verifyUser(payload, csrfToken, captchaToken).then((response: any) => {
        if (!response.error) {
          const email = response.payload.data.user.email;
          setUserPayload({
            ...userPayload,
            ...(email ? { email: email } : {}),
            email_otp_allowed: response.payload.data.email_otp_allowed,
          });
          if (props.smsDisabled) {
            setShowEmailLogin(true);
            if (email) {
              setPageType("verification");
            }
          } else {
            setPageType("verification");
          }
          setHidePopup(false);
        } else if (response.error.response.status == 404) {
          props.signUp(payload, csrfToken).then((response: any) => {
            if (!response.error) {
              const email = response.payload.data.email;
              if (email) {
                setUserPayload({
                  ...userPayload,
                  email: email,
                });
              }
              if (props.smsDisabled) {
                setShowEmailLogin(true);
                if (email) {
                  setPageType("verification");
                }
              } else {
                setPageType("verification");
              }
            } else {
              setSignInError(response.error.response.data);
            }
            setHidePopup(false);
          });
        } else {
          setSignInError(response.error.response.data);
          setHidePopup(false);
        }
      });
    } else {
      setSignInError({ phone_number: "Enter a valid phone number" });
      setHidePopup(false);
    }
  };

  const submitSignInViaEmail = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let isEmailValid = validateEmail(userPayload?.email);
    if (isEmailValid) {
      localStorage.setItem("userEmail", userPayload?.email);
      setPageType("verification");
    } else {
      setSignInError({ email: "Please enter a valid email" });
    }
  };

  const changeDialingCode = (dialingCode: any) => {
    setDialingCode(dialingCode);
  };

  const switchToEmailOtp = (status: boolean) => {
    setCaptchaToken("");
    setShowEmailLogin(status);
  }

  const onCaptchaTokenChange = (token: string) => {
    setCaptchaToken(token);
  }

  const onCaptchaErrored = () => {
    setCaptchaToken("");
  }

  const onCaptchaExpired = () => {
    setCaptchaToken("");
  }

  const onCaptchaWidgetIDChange = (widgetID: number) => {
    setCaptchaWidgetID(widgetID);
  }

  const disableLoginButton = useMemo(() => {
    return (
      submitButtonDisabled ||
      (!isTosChecked && props.company.country_code === "SG")
    );
  }, [isTosChecked, submitButtonDisabled, props.company.country_code]);

  const signInPage = (
    <React.Fragment>
      <div className="user-login px-3 py-3 mb-4 mt-4">
        {!showEmailLogin ? (
          <>
            <h1 className="header mb-1">
              {props.user.proceed_to_checkout ? (
                <FormattedMessage
                  id="registration.continue_to_checkout"
                  defaultMessage="Continue To Checkout"
                />
              ) : (
                <FormattedMessage
                  id="registration.sign_in_to_your_account"
                  defaultMessage="Sign In To Your Account"
                />
              )}
            </h1>
            <h3 className="mb-3 text-center">
              {props.user.proceed_to_checkout ? (
                <FormattedMessage
                  id="registration.please_enter_your_mobile_number"
                  defaultMessage="Please enter your mobile number"
                />
              ) : (
                <FormattedMessage
                  id="registration.sign_in_with_your_mobile_number"
                  defaultMessage="Sign in with your mobile number"
                />
              )}
            </h3>
            <div>
              <form onSubmit={submitSignIn}>
                <div className="form-group my-1">
                  <div className="input-group" dir="ltr">
                    <div className="input-group-prepend">
                      {props.company.country_code ? (
                        <DialingCodeSelector
                          dialingCode={dialingCode}
                          onChange={(dialingCode: any) => {
                            changeDialingCode(dialingCode);
                          }}
                          countryCode={props.company.country_code}
                        />
                      ) : null}
                    </div>
                    <FormattedMessage
                      id="registration.mobile_number"
                      defaultMessage="Mobile number"
                    >
                      {(placeholder) => (
                        <input
                          name="phone_number"
                          type="text"
                          inputMode="numeric"
                          placeholder={placeholder}
                          required
                          autoFocus
                          className="form-control custom-input"
                          onChange={handleInputChange}
                        ></input>
                      )}
                    </FormattedMessage>
                  </div>
                </div>
                {signInError ? (
                  <div className="input-label text-center text-danger">
                    {signInError["phone_number"] ? (
                      <FormattedMessage
                        id="registration.phone_number.validation"
                        defaultMessage="Enter a valid phone number"
                      />
                    ) : (
                      <FormattedMessage
                        id="error.oops_something_went_wrong"
                        defaultMessage="Oops! Something went wrong."
                      />
                    )}
                  </div>
                ) : null}
                <TermsOfServiceContainer
                  isTosChecked={isTosChecked}
                  isMarketingConsentChecked={isMarketingConsentChecked}
                  onTosChange={(checked: boolean) => setIsTosChecked(!isTosChecked)}
                  onMarketingConsentChange={(checked: boolean) => setIsMarketingConsentChecked(!isMarketingConsentChecked)}
                />
                {props.captchaEnabled && (
                  <div className="reCaptchaContainer">
                    <CheckboxReCaptchaEnterprise
                      onCaptchaTokenChange={onCaptchaTokenChange}
                      onCaptchaExpired={onCaptchaExpired}
                      onCaptchaErrored={onCaptchaErrored}
                      onCaptchaWidgetIDChange={onCaptchaWidgetIDChange}
                    />
                  </div>
                )}
                <button
                  className="button btn btn-primary w-100 py-2 mt-3"
                  type="submit"
                  disabled={disableLoginButton}
                >
                  <FormattedMessage id="global.next" defaultMessage="Next" />
                  <span className="ml-3">
                    <FontAwesomeIcon icon={faChevronCircleRight} />
                  </span>
                </button>
              </form>
            </div>
          </>
        ) : (
          <>
            <h1 className="header mb-4">
              <FormattedMessage
                id="registration.enter_email"
                defaultMessage="Enter your email address"
              />
            </h1>
            <div>
              <form onSubmit={submitSignInViaEmail}>
                <div className="form-group">
                  <FormattedMessage
                    id="registration.email.placeholder"
                    defaultMessage="example@email.com"
                  >
                    {(placeholder) => (
                      <input
                        type="text"
                        name="email"
                        ref={emailRef}
                        required
                        autoFocus
                        placeholder={placeholder.toString()}
                        className="email-input form-control"
                        onChange={handleInputChange}
                      />
                    )}
                  </FormattedMessage>
                </div>
                {signInError ? (
                  <div className="input-label text-center text-danger">
                    {signInError["email"] ? (
                      <FormattedMessage
                        id="registration.email.validation"
                        defaultMessage="Please enter a valid email address."
                      />
                    ) : null}
                  </div>
                ) : null}
                {props.captchaEnabled && (
                  <div className="reCaptchaContainer">
                    <CheckboxReCaptchaEnterprise
                      onCaptchaTokenChange={onCaptchaTokenChange}
                      onCaptchaExpired={onCaptchaExpired}
                      onCaptchaErrored={onCaptchaErrored}
                      onCaptchaWidgetIDChange={onCaptchaWidgetIDChange}
                    />
                  </div>
                )}
                <button
                  className="button btn btn-primary w-100 py-2 mt-3"
                  type="submit"
                  disabled={submitButtonDisabled}
                >
                  <FormattedMessage
                    id="global.continue"
                    defaultMessage="Continue"
                  />
                  <span className="ml-3">
                    <FontAwesomeIcon icon={faChevronCircleRight} />
                  </span>
                </button>
              </form>
            </div>
            {!props.smsDisabled ? (
              <p
                className="change-otp-mode-sms"
                onClick={() => switchToEmailOtp(false)}
              >
                <FormattedMessage
                  id="registration.otp.send_via_sms"
                  defaultMessage="Send via SMS"
                />
              </p>
            ) : null}
          </>
        )}
      </div>
    </React.Fragment>
  );

  return (
    <Modal
      show={show}
      dialogClassName={
        hidePopup ? "custom custom-login curved invisible" : "custom custom-login curved"
      }
      onHide={handleClose}
      centered
      backdropClassName={hidePopup ? "invisible" : ""}
      backdrop={hidePopup ? "static": true}
    >
      <ModalCloseIcon onClick={handleClose} />
      {pageType == "verification" ? (
        <MobileVerification
          onClose={() => handleClose()}
          hideModal={(status: boolean) => setHidePopup(status)}
          proceedToCheckout={props.user.proceed_to_checkout}
          setShowEmailOtp={switchToEmailOtp}
          showEmailOtp={showEmailLogin}
          setPageType={setPageType}
          userPayload={userPayload}
        />
      ) : (
        signInPage
      )}
    </Modal>
  );
}

const mapStateToProps = (state: any) => {
  let company = state.company || {};
  let user = state.user || {};
  let captchaEnabled = company.configuration?.captcha_enabled || false;
  let smsDisabled = company?.sms_disabled || false;
  return {
    company,
    user,
    captchaEnabled,
    smsDisabled
  };
};

const mapDispatchToProps = {
  verifyUser: userOperations.verifyUser,
  signUp: userOperations.signUp,
};

export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
